import { useEffect, useState } from "react";
import { api, formatTL, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Car, Fuel, Gauge, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ViewToggle, useViewMode } from "@/components/ViewToggle";

export default function VehiclesPage() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [mode, setMode] = useViewMode("vehicles");
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [openVeh, setOpenVeh] = useState(false);
  const [openLog, setOpenLog] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vform, setVform] = useState({ plate: "", brand: "", model: "", year: "", assigned_to: "", initial_km: "" });
  const [lform, setLform] = useState({ vehicle_id: "", log_type: "FUEL", km: "", fuel_liters: "", fuel_cost: "", note: "" });

  const isBroker = user?.role === "BROKER" || user?.role === "SUPER_ADMIN";

  const load = async () => {
    const { data: v } = await api.get("/vehicles");
    setVehicles(v);
    const { data: l } = await api.get("/vehicle-logs");
    setLogs(l);
    if (isBroker) {
      const { data: u } = await api.get("/users");
      setUsers(u);
    }
  };

  useEffect(() => { load(); }, [user?.role]);

  const saveVehicle = async (e) => {
    e.preventDefault();
    try {
      await api.post("/vehicles", {
        ...vform,
        year: vform.year ? parseInt(vform.year) : null,
        initial_km: parseFloat(vform.initial_km || 0),
        assigned_to: vform.assigned_to || null,
      });
      toast.success("Araç eklendi");
      setOpenVeh(false);
      setVform({ plate: "", brand: "", model: "", year: "", assigned_to: "", initial_km: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openLogFor = (v) => {
    setSelectedVehicle(v);
    setLform({ vehicle_id: v.id, log_type: "FUEL", km: v.current_km || "", fuel_liters: "", fuel_cost: "", note: "" });
    setOpenLog(true);
  };

  const saveLog = async (e) => {
    e.preventDefault();
    try {
      await api.post("/vehicle-logs", {
        ...lform,
        km: lform.km ? parseFloat(lform.km) : null,
        fuel_liters: lform.fuel_liters ? parseFloat(lform.fuel_liters) : null,
        fuel_cost: lform.fuel_cost ? parseFloat(lform.fuel_cost) : null,
      });
      toast.success("Kayıt eklendi");
      setOpenLog(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm("Araç ve kayıtları silinecek?")) return;
    await api.delete(`/vehicles/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Filo</div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Araç Takibi</h1>
          <p className="text-slate-500 mt-1">Km ve yakıt giderlerini takip edin.</p>
        </div>
        {isBroker && (
          <Button data-testid="add-vehicle-btn" onClick={() => setOpenVeh(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" /> Yeni Araç
          </Button>
        )}
      </div>

      <Tabs defaultValue="vehicles">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <TabsList>
            <TabsTrigger value="vehicles">Araçlar</TabsTrigger>
            <TabsTrigger value="logs">Kayıtlar</TabsTrigger>
          </TabsList>
          <ViewToggle mode={mode} onChange={setMode} />
        </div>
        <TabsContent value="vehicles" className="space-y-4 mt-4">
          {vehicles.length === 0 ? (
            <Card className="p-16 text-center text-slate-500 border-dashed"><Car className="w-10 h-10 mx-auto mb-3 text-slate-300" />Henüz araç tanımlı değil.</Card>
          ) : mode === "list" ? (
            <Card className="overflow-x-auto" data-testid="vehicles-list-view">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>{["Plaka", "Araç", "Yıl", "Zimmet", "Güncel Km", "Yakıt (L)", "Yakıt Gideri", ""].map((h, i) => <th key={i} className="text-left px-4 py-3">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {vehicles.map((v) => (
                    <tr key={v.id} data-testid={`vehicle-row-${v.id}`} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-mono font-semibold">{v.plate}</td>
                      <td className="px-4 py-2.5">{v.brand} {v.model}</td>
                      <td className="px-4 py-2.5">{v.year || "-"}</td>
                      <td className="px-4 py-2.5 text-slate-600">{v.assigned_name || "Atanmamış"}</td>
                      <td className="px-4 py-2.5 font-semibold">{(v.current_km || 0).toLocaleString("tr-TR")}</td>
                      <td className="px-4 py-2.5">{(v.total_fuel_liters || 0).toLocaleString("tr-TR")}</td>
                      <td className="px-4 py-2.5 font-bold text-red-700">{formatTL(v.total_fuel_cost)}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex gap-1">
                          <Button size="sm" data-testid={`log-vehicle-row-${v.id}`} onClick={() => openLogFor(v)} className="h-8 bg-emerald-600 hover:bg-emerald-700"><Plus className="w-3 h-3 mr-1" />Kayıt</Button>
                          {isBroker && <Button size="sm" variant="outline" className="h-8" onClick={() => deleteVehicle(v.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="vehicles-grid-view">
              {vehicles.map((v) => (
                <Card key={v.id} className="p-5 hover:shadow-md transition">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-mono text-xs text-slate-500">{v.plate}</div>
                      <div className="font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{v.brand} {v.model}</div>
                      <div className="text-xs text-slate-500">{v.year || "-"} • {v.assigned_name || "Atanmamış"}</div>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                      <Car className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                    <div className="p-2 rounded-lg bg-slate-50">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Gauge className="w-3 h-3" />Km</div>
                      <div className="font-bold">{(v.current_km || 0).toLocaleString("tr-TR")}</div>
                    </div>
                    <div className="p-2 rounded-lg bg-slate-50">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Fuel className="w-3 h-3" />Yakıt</div>
                      <div className="font-bold text-red-700">{formatTL(v.total_fuel_cost)}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" data-testid={`log-vehicle-${v.id}`} onClick={() => openLogFor(v)} className="flex-1 bg-emerald-600 hover:bg-emerald-700"><Plus className="w-3 h-3 mr-1" />Kayıt</Button>
                    {isBroker && <Button size="sm" variant="outline" onClick={() => deleteVehicle(v.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="logs" className="mt-4">
          <Card className="divide-y divide-slate-100">
            {logs.length === 0 ? (
              <div className="p-12 text-center text-slate-400">Henüz kayıt yok.</div>
            ) : logs.map((l) => {
              const v = vehicles.find((x) => x.id === l.vehicle_id);
              return (
                <div key={l.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${l.log_type === "FUEL" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
                      {l.log_type === "FUEL" ? <Fuel className="w-4 h-4" /> : <Gauge className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{v?.plate || "Araç"} - <Badge variant="outline">{l.log_type === "FUEL" ? "Yakıt" : "Km"}</Badge></div>
                      <div className="text-xs text-slate-500">{new Date(l.date).toLocaleDateString("tr-TR")} {l.note && `• ${l.note}`}</div>
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    {l.km && <div>Km: <span className="font-bold">{l.km.toLocaleString("tr-TR")}</span></div>}
                    {l.fuel_liters && <div>{l.fuel_liters}L</div>}
                    {l.fuel_cost && <div className="text-red-700 font-bold">{formatTL(l.fuel_cost)}</div>}
                  </div>
                </div>
              );
            })}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={openVeh} onOpenChange={setOpenVeh}>
        <DialogContent>
          <DialogHeader><DialogTitle>Yeni Araç</DialogTitle></DialogHeader>
          <form onSubmit={saveVehicle} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Plaka *</Label><Input required value={vform.plate} onChange={(e) => setVform({ ...vform, plate: e.target.value })} /></div>
              <div><Label>Yıl</Label><Input type="number" value={vform.year} onChange={(e) => setVform({ ...vform, year: e.target.value })} /></div>
              <div><Label>Marka *</Label><Input required value={vform.brand} onChange={(e) => setVform({ ...vform, brand: e.target.value })} /></div>
              <div><Label>Model *</Label><Input required value={vform.model} onChange={(e) => setVform({ ...vform, model: e.target.value })} /></div>
              <div><Label>Başlangıç Km</Label><Input type="number" value={vform.initial_km} onChange={(e) => setVform({ ...vform, initial_km: e.target.value })} /></div>
              <div>
                <Label>Zimmetli Danışman</Label>
                <Select value={vform.assigned_to} onValueChange={(v) => setVform({ ...vform, assigned_to: v })}>
                  <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpenVeh(false)}>İptal</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={openLog} onOpenChange={setOpenLog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{selectedVehicle?.plate} - Km/Yakıt Kaydı</DialogTitle></DialogHeader>
          <form onSubmit={saveLog} className="space-y-3">
            <div>
              <Label>Tür</Label>
              <Select value={lform.log_type} onValueChange={(v) => setLform({ ...lform, log_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FUEL">Yakıt</SelectItem>
                  <SelectItem value="KM">Sadece Km</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Km (Yeni Kilometre)</Label><Input type="number" value={lform.km} onChange={(e) => setLform({ ...lform, km: e.target.value })} /></div>
            {lform.log_type === "FUEL" && (
              <>
                <div><Label>Yakıt (Litre)</Label><Input type="number" step="0.01" value={lform.fuel_liters} onChange={(e) => setLform({ ...lform, fuel_liters: e.target.value })} /></div>
                <div><Label>Yakıt Tutarı (TL)</Label><Input type="number" step="0.01" value={lform.fuel_cost} onChange={(e) => setLform({ ...lform, fuel_cost: e.target.value })} /></div>
              </>
            )}
            <div><Label>Not</Label><Textarea rows={2} value={lform.note} onChange={(e) => setLform({ ...lform, note: e.target.value })} /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setOpenLog(false)}>İptal</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
